import { Maybe } from 'types/UtilityTypes';

// Vehicle types accepted by POST /quote/quickquote/v2. The backend rejects
// Super Dispatch catalog names like 2_door_coupe, so map those before submit.
export enum AutoVehicleType {
  Sedan = 'sedan',
  Coupe2Doors = 'coupe_2_doors',
  Suv = 'suv',
  Pickup2Doors = 'pickup_2_doors',
  Pickup4Doors = 'pickup_4_doors',
  Van = 'van',
}

export const AUTO_VEHICLE_TYPE_OPTIONS = [
  { label: 'Sedan', value: AutoVehicleType.Sedan },
  { label: 'Coupe (2 doors)', value: AutoVehicleType.Coupe2Doors },
  { label: 'SUV', value: AutoVehicleType.Suv },
  { label: 'Pickup (2 doors)', value: AutoVehicleType.Pickup2Doors },
  { label: 'Pickup (4 doors)', value: AutoVehicleType.Pickup4Doors },
  { label: 'Van', value: AutoVehicleType.Van },
] as const;

// Keep in sync with minAutoVehicleYear and maxAutoTransportVehicles in the
// backend (common/models/auto_transport.go), so the form and API accept the
// same values.
export const MIN_AUTO_VEHICLE_YEAR = 1900;
// Nine is the most cars that fit on a car hauler.
export const MAX_AUTO_VEHICLES = 9;

export function maxAutoVehicleYear(now = new Date()): number {
  return now.getFullYear() + 1;
}

export function isAutoVehicleYear(value: string): boolean {
  if (!/^\d{4}$/.test(value)) {
    return false;
  }

  const year = Number(value);
  return year >= MIN_AUTO_VEHICLE_YEAR && year <= maxAutoVehicleYear();
}

const AUTO_VEHICLE_TYPE_VALUES = new Set<string>(
  Object.values(AutoVehicleType)
);

// Drumkit enum for API submit; accepts canonical values and SD catalog aliases.
export function canonicalAutoVehicleType(
  vehicleType: string
): Maybe<AutoVehicleType> {
  const trimmed = vehicleType.trim();
  if (!trimmed) {
    return null;
  }

  if (AUTO_VEHICLE_TYPE_VALUES.has(trimmed)) {
    return trimmed as AutoVehicleType;
  }

  return mapSuperDispatchVehicleType(trimmed);
}

export function mapSuperDispatchVehicleType(
  vehicleType: string
): Maybe<AutoVehicleType> {
  switch (vehicleType.trim().toLowerCase()) {
    case 'sedan':
      return AutoVehicleType.Sedan;
    case '2_door_coupe':
    case 'coupe_2_doors':
      return AutoVehicleType.Coupe2Doors;
    case 'suv':
      return AutoVehicleType.Suv;
    case 'pickup_2_doors':
      return AutoVehicleType.Pickup2Doors;
    case '4_door_pickup':
    case 'pickup_4_doors':
      return AutoVehicleType.Pickup4Doors;
    case 'van':
      return AutoVehicleType.Van;
    default:
      return null;
  }
}

export function autoVehicleTypeLabel(vehicleType: string): string {
  const mapped = mapSuperDispatchVehicleType(vehicleType);
  const option = AUTO_VEHICLE_TYPE_OPTIONS.find(
    (item) => item.value === mapped
  );
  return option?.label ?? vehicleType;
}
